// src/features/dashboard/services/Manager/idleTimeReport.service.ts

import api from '../../../../api/apiService';
import { ENDPOINTS } from '../../../../api/endpoints';
import { ENV } from '../../../../config/env';

import type {
  IdleTimeRequest,
  IdleTimeKpiRequest,
  IdleTimeKpiResponse,
  IdleTeamChartResponse,
  IdleBreakdownResponse,
  IdleTrendResponse,
  AssociateIdleDetail,
} from '../../types/Manager/idleTime.types';

import {
  idleKpiMock,
  idleTeamChartMock,
  idleBreakdownMock,
  idleTrendMock,
  associateIdleDetailsMock,
} from '../../mocks/Manager/idleTime.mock';


// ============================================================
// GENERIC HELPERS
// ============================================================

type UnknownRecord = Record<string, unknown>;


// ============================================================
// GET PROPERTY
//
// Supports:
// camelCase
// PascalCase
// snake_case
// ============================================================

const getProperty = (
  item: unknown,
  ...keys: string[]
): unknown => {
  if (
    !item ||
    typeof item !== 'object'
  ) {
    return undefined;
  }

  const object =
    item as UnknownRecord;

  // Direct lookup
  for (const key of keys) {
    if (key in object) {
      return object[key];
    }
  }

  // Case-insensitive lookup
  const objectKeys =
    Object.keys(object);

  for (const key of keys) {
    const foundKey =
      objectKeys.find(
        (objectKey) =>
          objectKey.toLowerCase() ===
          key.toLowerCase(),
      );

    if (foundKey) {
      return object[foundKey];
    }
  }

  return undefined;
};


// ============================================================
// SAFE STRING
// ============================================================

const safeString = (
  value: unknown,
  fallback = '',
): string => {
  if (
    value === null ||
    value === undefined
  ) {
    return fallback;
  }

  const result =
    String(value).trim();

  return result || fallback;
};


// ============================================================
// SAFE NUMBER
// ============================================================

const safeNumber = (
  value: unknown,
  fallback = 0,
): number => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return fallback;
  }

  const numberValue =
    typeof value === 'number'
      ? value
      : Number(value);

  if (
    !Number.isFinite(numberValue)
  ) {
    return fallback;
  }

  return numberValue;
};


// ============================================================
// ARRAY RESPONSE NORMALIZER
//
// Supports:
// []
// { data: [] }
// { result: [] }
// { items: [] }
// { records: [] }
// { value: [] }
// { Data: [] }
// { Result: [] }
// etc.
// ============================================================

const unwrapArrayResponse = <T>(
  response: unknown,
): T[] => {
  if (Array.isArray(response)) {
    return response as T[];
  }

  if (
    !response ||
    typeof response !== 'object'
  ) {
    return [];
  }

  const possibleKeys = [
    'data',
    'Data',
    'result',
    'Result',
    'items',
    'Items',
    'records',
    'Records',
    'value',
    'Value',
    'list',
    'List',
  ];

  for (const key of possibleKeys) {
    const value =
      getProperty(
        response,
        key,
      );

    if (Array.isArray(value)) {
      return value as T[];
    }
  }

  return [];
};


// ============================================================
// OBJECT RESPONSE NORMALIZER
//
// Supports:
// { ...object }
// { data: {...} }
// { result: {...} }
// { value: {...} }
// ============================================================

const unwrapObjectResponse = <T>(
  response: unknown,
): T | null => {
  if (
    !response ||
    typeof response !== 'object'
  ) {
    return null;
  }

  if (Array.isArray(response)) {
    return null;
  }

  const object =
    response as UnknownRecord;

  const possibleKeys = [
    'data',
    'Data',
    'result',
    'Result',
    'item',
    'Item',
    'record',
    'Record',
    'value',
    'Value',
  ];

  for (const key of possibleKeys) {
    const value =
      getProperty(
        object,
        key,
      );

    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      return value as T;
    }
  }

  return object as T;
};


// ============================================================
// KPI NORMALIZER
// ============================================================

const normalizeKpiResponse = (
  item: unknown,
): IdleTimeKpiResponse => {
  return {
    spanIdlePercentage:
      safeNumber(
        getProperty(
          item,
          'spanIdlePercentage',
          'SpanIdlePercentage',
        ),
      ),

    previousSpanIdlePercentage:
      safeNumber(
        getProperty(
          item,
          'previousSpanIdlePercentage',
          'PreviousSpanIdlePercentage',
        ),
      ),

    spanIdleTrend:
      safeNumber(
        getProperty(
          item,
          'spanIdleTrend',
          'SpanIdleTrend',
        ),
      ),

    averageIdleTime:
      safeString(
        getProperty(
          item,
          'averageIdleTime',
          'AverageIdleTime',
        ),
        '0',
      ),

    withinTarget:
      safeNumber(
        getProperty(
          item,
          'withinTarget',
          'WithinTarget',
        ),
      ),

    exceedingTarget:
      safeNumber(
        getProperty(
          item,
          'exceedingTarget',
          'ExceedingTarget',
        ),
      ),

    teamLeadCode:
      safeString(
        getProperty(
          item,
          'teamLeadCode',
          'TeamLeadCode',
        ),
      ),

    teamLeadName:
      safeString(
        getProperty(
          item,
          'teamLeadName',
          'TeamLeadName',
          'managerName',
          'ManagerName',
        ),
        'Unknown Team',
      ),

    associates:
      safeNumber(
        getProperty(
          item,
          'associates',
          'Associates',
        ),
      ),

    bestTeamIdlePercentage:
      safeNumber(
        getProperty(
          item,
          'bestTeamIdlePercentage',
          'BestTeamIdlePercentage',
        ),
      ),

    targetPercentage:
      safeNumber(
        getProperty(
          item,
          'targetPercentage',
          'TargetPercentage',
        ),
      ),

    totalAssociates:
      safeNumber(
        getProperty(
          item,
          'totalAssociates',
          'TotalAssociates',
        ),
      ),

    trendDirection:
      safeString(
        getProperty(
          item,
          'trendDirection',
          'TrendDirection',
        ),
      ),

    bestTeamStatus:
      safeString(
        getProperty(
          item,
          'bestTeamStatus',
          'BestTeamStatus',
        ),
      ),
  };
};


// ============================================================
// TEAM CHART NORMALIZER
// ============================================================

const normalizeTeamChartItem = (
  item: unknown,
): IdleTeamChartResponse => {
  return {
    teamLeadCode:
      safeString(
        getProperty(
          item,
          'teamLeadCode',
          'TeamLeadCode',
        ),
      ),

    teamLeadName:
      safeString(
        getProperty(
          item,
          'teamLeadName',
          'TeamLeadName',
          'managerName',
          'ManagerName',
        ),
        'Unknown Team',
      ),

    associateCount:
      safeNumber(
        getProperty(
          item,
          'associateCount',
          'AssociateCount',
          'associates',
          'Associates',
        ),
      ),

    thisWeekIdle:
      safeNumber(
        getProperty(
          item,
          'thisWeekIdle',
          'ThisWeekIdle',
        ),
      ),

    lastWeekIdle:
      safeNumber(
        getProperty(
          item,
          'lastWeekIdle',
          'LastWeekIdle',
        ),
      ),

    targetPercentage:
      safeNumber(
        getProperty(
          item,
          'targetPercentage',
          'TargetPercentage',
        ),
      ),

    currentWeekLoggedHours:
      safeString(
        getProperty(
          item,
          'currentWeekLoggedHours',
          'CurrentWeekLoggedHours',
        ),
        '0',
      ),

    currentWeekIdleHours:
      safeString(
        getProperty(
          item,
          'currentWeekIdleHours',
          'CurrentWeekIdleHours',
        ),
        '0',
      ),

    previousWeekLoggedHours:
      safeString(
        getProperty(
          item,
          'previousWeekLoggedHours',
          'PreviousWeekLoggedHours',
        ),
        '0',
      ),

    previousWeekIdleHours:
      safeString(
        getProperty(
          item,
          'previousWeekIdleHours',
          'PreviousWeekIdleHours',
        ),
        '0',
      ),
  };
};


// ============================================================
// BREAKDOWN NORMALIZER
// ============================================================

const normalizeBreakdownItem = (
  item: unknown,
): IdleBreakdownResponse => {
  return {
    masterActivityCategoryId:
      safeNumber(
        getProperty(
          item,
          'masterActivityCategoryId',
          'MasterActivityCategoryId',
        ),
      ),

    categoryName:
      safeString(
        getProperty(
          item,
          'categoryName',
          'CategoryName',
        ),
        'Unknown',
      ),

    subCategoryName:
      safeString(
        getProperty(
          item,
          'subCategoryName',
          'SubCategoryName',
        ),
        '-',
      ),

    colorHex:
      safeString(
        getProperty(
          item,
          'colorHex',
          'ColorHex',
          'color',
          'Color',
        ),
        '#94A3B8',
      ),

    isProductive:
      Boolean(
        getProperty(
          item,
          'isProductive',
          'IsProductive',
        ),
      ),

    isNonProductive:
      Boolean(
        getProperty(
          item,
          'isNonProductive',
          'IsNonProductive',
        ),
      ),

    isNoImpact:
      Boolean(
        getProperty(
          item,
          'isNoImpact',
          'IsNoImpact',
        ),
      ),

    associateCount:
      safeNumber(
        getProperty(
          item,
          'associateCount',
          'AssociateCount',
        ),
      ),

    activityCount:
      safeNumber(
        getProperty(
          item,
          'activityCount',
          'ActivityCount',
        ),
      ),

    totalDurationSeconds:
      safeNumber(
        getProperty(
          item,
          'totalDurationSeconds',
          'TotalDurationSeconds',
        ),
      ),

    totalMinutes:
      safeNumber(
        getProperty(
          item,
          'totalMinutes',
          'TotalMinutes',
        ),
      ),

    totalHours:
      safeNumber(
        getProperty(
          item,
          'totalHours',
          'TotalHours',
        ),
      ),

    percentage:
      safeNumber(
        getProperty(
          item,
          'percentage',
          'Percentage',
        ),
      ),
  };
};


// ============================================================
// TREND NORMALIZER
// ============================================================

const normalizeTrendItem = (
  item: unknown,
): IdleTrendResponse => {
  return {
    teamLeadCode:
      safeString(
        getProperty(
          item,
          'teamLeadCode',
          'TeamLeadCode',
        ),
      ),

    teamLeadName:
      safeString(
        getProperty(
          item,
          'teamLeadName',
          'TeamLeadName',
          'managerName',
          'ManagerName',
        ),
        'Unknown Team',
      ),

    loggedDate:
      safeString(
        getProperty(
          item,
          'loggedDate',
          'LoggedDate',
          'date',
          'Date',
        ),
      ),

    totalLoggedHours:
      safeString(
        getProperty(
          item,
          'totalLoggedHours',
          'TotalLoggedHours',
        ),
        '0',
      ),

    totalActiveHours:
      safeString(
        getProperty(
          item,
          'totalActiveHours',
          'TotalActiveHours',
        ),
        '0',
      ),

    totalIdleHours:
      safeString(
        getProperty(
          item,
          'totalIdleHours',
          'TotalIdleHours',
        ),
        '0',
      ),

    totalTimeOnSystem:
      safeString(
        getProperty(
          item,
          'totalTimeOnSystem',
          'TotalTimeOnSystem',
        ),
        '0',
      ),

    totalTimeAwayFromSystem:
      safeString(
        getProperty(
          item,
          'totalTimeAwayFromSystem',
          'TotalTimeAwayFromSystem',
        ),
        '0',
      ),

    idlePercentage:
      safeNumber(
        getProperty(
          item,
          'idlePercentage',
          'IdlePercentage',
        ),
      ),

    activePercentage:
      safeNumber(
        getProperty(
          item,
          'activePercentage',
          'ActivePercentage',
        ),
      ),
  };
};


// ============================================================
// ASSOCIATE NORMALIZER
// ============================================================

const normalizeAssociateItem = (
  item: unknown,
): AssociateIdleDetail => {
  const result: AssociateIdleDetail = {
    // --------------------------------------------------------
    // EMPLOYEE CODE
    // --------------------------------------------------------

    employeeCode: safeString(
      getProperty(
        item,
        'employeeCode',
        'EmployeeCode',
        'employeeId',
        'EmployeeId',
      ),
    ),

    // --------------------------------------------------------
    // EMPLOYEE NAME
    // --------------------------------------------------------

    fullName: safeString(
      getProperty(
        item,
        'fullName',
        'FullName',
        'employeeName',
        'EmployeeName',
        'name',
        'Name',
      ),
    ),

    // --------------------------------------------------------
    // TEAM LEAD CODE
    // --------------------------------------------------------

    teamLeadCode: safeString(
      getProperty(
        item,
        'teamLeadCode',
        'TeamLeadCode',
        'teamLeadId',
        'TeamLeadId',
      ),
    ),

    // --------------------------------------------------------
    // TEAM LEAD NAME
    // --------------------------------------------------------

    teamLeadName: safeString(
      getProperty(
        item,
        'teamLeadName',
        'TeamLeadName',
        'managerName',
        'ManagerName',
      ),
      'Unknown Team',
    ),

    // --------------------------------------------------------
    // DEPARTMENT
    // --------------------------------------------------------

    department: safeString(
      getProperty(
        item,
        'department',
        'Department',
      ),
    ),

    // --------------------------------------------------------
    // LOGGED DATE
    // --------------------------------------------------------

    loggedDate: safeString(
      getProperty(
        item,
        'loggedDate',
        'LoggedDate',
        'date',
        'Date',
      ),
    ),

    // --------------------------------------------------------
    // SHIFT
    // --------------------------------------------------------

    shiftName: safeString(
      getProperty(
        item,
        'shiftName',
        'ShiftName',
        'shift',
        'Shift',
      ),
    ),

    // --------------------------------------------------------
    // ATTENDANCE
    // --------------------------------------------------------

    attendanceStatus: safeString(
      getProperty(
        item,
        'attendanceStatus',
        'AttendanceStatus',
      ),
    ),

    // --------------------------------------------------------
    // LOGGED HOURS
    // --------------------------------------------------------

    loggedHours: safeString(
      getProperty(
        item,
        'loggedHours',
        'LoggedHours',
      ),
      '0',
    ),

    // --------------------------------------------------------
    // ACTIVE HOURS
    // --------------------------------------------------------

    activeHours: safeString(
      getProperty(
        item,
        'activeHours',
        'ActiveHours',
      ),
      '0',
    ),

    // --------------------------------------------------------
    // IDLE HOURS
    // --------------------------------------------------------

    idleHours: safeString(
      getProperty(
        item,
        'idleHours',
        'IdleHours',
      ),
      '0',
    ),

    // --------------------------------------------------------
    // TIME ON SYSTEM
    // --------------------------------------------------------

    timeOnSystem: safeString(
      getProperty(
        item,
        'timeOnSystem',
        'TimeOnSystem',
      ),
      '0',
    ),

    // --------------------------------------------------------
    // TIME AWAY FROM SYSTEM
    // --------------------------------------------------------

    timeAwayFromSystem: safeString(
      getProperty(
        item,
        'timeAwayFromSystem',
        'TimeAwayFromSystem',
      ),
      '0',
    ),

    // --------------------------------------------------------
    // IDLE PERCENTAGE
    // --------------------------------------------------------

    idlePercentage: safeNumber(
      getProperty(
        item,
        'idlePercentage',
        'IdlePercentage',
      ),
    ),

    // --------------------------------------------------------
    // IDLE STATUS
    // --------------------------------------------------------

    idleStatus: safeString(
      getProperty(
        item,
        'idleStatus',
        'IdleStatus',
      ),
    ),

    // --------------------------------------------------------
    // STATUS
    // --------------------------------------------------------

    status: safeString(
      getProperty(
        item,
        'status',
        'Status',
      ),
    ),
  };

  return result;
};


// ============================================================
// KPI
// ============================================================

export const getIdleTimeKpi = async (
  payload: IdleTimeKpiRequest,
): Promise<IdleTimeKpiResponse> => {
  if (ENV.USE_MOCK) {
    return Promise.resolve(
      idleKpiMock,
    );
  }

  const response =
    await api.post<unknown>(
      ENDPOINTS.IDLE_TIME_KPI_CARD,
      payload,
    );

  console.log(
    'Idle KPI RAW API RESPONSE:',
    response.data,
  );

  const raw =
    unwrapObjectResponse<unknown>(
      response.data,
    );

  const result =
    normalizeKpiResponse(raw);

  console.log(
    'Idle KPI NORMALIZED:',
    result,
  );

  return result;
};


// ============================================================
// TEAM CHART
// ============================================================

export const getIdleTeamChart = async (
  payload: IdleTimeRequest,
): Promise<IdleTeamChartResponse[]> => {
  if (ENV.USE_MOCK) {
    return Promise.resolve(
      idleTeamChartMock,
    );
  }

  const response =
    await api.post<unknown>(
      ENDPOINTS.IDLE_TIME_TEAM_CHART,
      payload,
    );

  console.log(
    'Idle Team RAW API RESPONSE:',
    response.data,
  );

  const raw =
    unwrapArrayResponse<unknown>(
      response.data,
    );

  const result =
    raw.map(
      normalizeTeamChartItem,
    );

  console.log(
    'Idle Team NORMALIZED:',
    result,
  );

  return result;
};


// ============================================================
// BREAKDOWN
// ============================================================

export const getIdleBreakdown = async (
  payload: IdleTimeRequest,
): Promise<IdleBreakdownResponse[]> => {
  if (ENV.USE_MOCK) {
    return Promise.resolve(
      idleBreakdownMock,
    );
  }

  const response =
    await api.post<unknown>(
      ENDPOINTS.IDLE_TIME_BREAKDOWN,
      payload,
    );

  console.log(
    'Idle Breakdown RAW API RESPONSE:',
    response.data,
  );

  const raw =
    unwrapArrayResponse<unknown>(
      response.data,
    );

  const result =
    raw.map(
      normalizeBreakdownItem,
    );

  console.log(
    'Idle Breakdown NORMALIZED:',
    result,
  );

  return result;
};


// ============================================================
// DAILY IDLE TREND
// ============================================================

export const getIdleTrend = async (
  payload: IdleTimeRequest,
): Promise<IdleTrendResponse[]> => {
  if (ENV.USE_MOCK) {
    return Promise.resolve(
      idleTrendMock,
    );
  }

  const response =
    await api.post<unknown>(
      ENDPOINTS.IDLE_TIME_TREND,
      payload,
    );

  console.log(
    'Idle Trend RAW API RESPONSE:',
    response.data,
  );

  const raw =
    unwrapArrayResponse<unknown>(
      response.data,
    );

  const result =
    raw
      .map(
        normalizeTrendItem,
      )
      .filter(
        (item) =>
          Boolean(item.loggedDate),
      );

  console.log(
    'Idle Trend NORMALIZED:',
    result,
  );

  return result;
};


// ============================================================
// ASSOCIATE DETAILS
// ============================================================

export const getAssociateIdleDetails =
  async (
    payload: IdleTimeRequest,
  ): Promise<AssociateIdleDetail[]> => {
    if (ENV.USE_MOCK) {
      return Promise.resolve(
        associateIdleDetailsMock,
      );
    }

    const response =
      await api.post<unknown>(
        ENDPOINTS.ASSOCIATE_IDLE_DETAILS,
        payload,
      );

    console.log(
      'Associate Details RAW API RESPONSE:',
      response.data,
    );

    const raw =
      unwrapArrayResponse<unknown>(
        response.data,
      );

    const result =
      raw.map(
        normalizeAssociateItem,
      );

    console.log(
      'Associate Details NORMALIZED:',
      result,
    );

    return result;
  };